// imports
import clsx from 'clsx';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { LINKS } from '../../constants';
import InfoTooltip from '../tooltip/info';
import { ICON, IMAGE } from '../../assets';
import { DATE_UTILS } from '../../common/utils';
import { Quest, QuestStatus } from '../../common/types';
import { selectConfig, selectUserQuests, selectUIQuestProgress } from '../../repositories/store/slices';

// interfaces / types / consts
interface Props {
  quest: Quest;
  selected: boolean;
  onClicked: () => void;
}

// exports
export default function QuestCard(props: Props): JSX.Element {
  const {
    quest: {
      status, duration,
      id, name, pinId, rewards
    }, selected, onClicked
  } = props;
  const isLocked = (status === QuestStatus.locked);
  const isAvailable = (status === QuestStatus.available);
  const isCompleted = (status === QuestStatus.completed);
  const isInProgress = (status === QuestStatus.inProgress);
  let predecessor = '';
  if (isLocked) {
    const questNumber = Number(id.split('camp_')[1]);
    predecessor = `camp_${`${questNumber - 1}`.padStart(3, '0')}`;
  }

  // hooks configuration
  const [isWiggling, setIsWiggling] = useState(false);
  const { world, items } = useSelector(selectConfig);
  const { data: quests } = useSelector(selectUserQuests);
  const questsProgress = useSelector(selectUIQuestProgress);

  // private methods
  const wiggle = (): void => {
    setIsWiggling(true);
    setTimeout(() => { setIsWiggling(false); }, 1000);
  };

  let questThemeIcon = '';
  if (isLocked) {
    questThemeIcon = ICON.QUEST_THEME_LOCKED;
  } else if (isCompleted) {
    questThemeIcon = ICON.QUEST_THEME_COMPLETE;
  } else {
    questThemeIcon = ICON.QUEST_THEME;
  }

  // render
  return (
    <button type="button" id={`quest-card-${id}`} className="quest-card-container" onClick={isLocked ? () => wiggle() : onClicked}>
      <div className={clsx('card', isLocked && 'locked', selected && 'selected', isCompleted && 'complete')}>
        <div className="card-row">
          {!isCompleted && <img className="icon" src={questThemeIcon} alt="quest" />}
          <div className={clsx('details', isCompleted && 'completed')}>
            <div className="name">{name}</div>
            <div className="region">{world.islands[world.pins[pinId].islandId].name}</div>
            {isLocked && <div className="predecessor">{`Requires: ${quests[predecessor || '']?.name}`}</div>}
            {isLocked || (isInProgress && questsProgress[id]?.time === '0s') ? (<div />)
              : (
                <div className="duration">
                  {(isAvailable || (isInProgress && questsProgress[id]?.time !== '0s')) && <img src={ICON.QUEST_DURATION_BLUE} alt="duration" />}
                  {isCompleted && (<span className="complete">Complete!</span>)}
                  {isInProgress && questsProgress[id]?.time !== '0s' && (
                    <>
                      <span className="time">{questsProgress[id]?.time}</span>
                      {' '}
                      <span className="text">Left</span>
                      <div className="progress-container"><div className="progress-bar" style={{ width: `${questsProgress[id]?.progress}%` }} /></div>
                    </>
                  )}
                  {isAvailable && <span>{`Duration: ${DATE_UTILS.formatTime(duration * 1000)}`}</span>}
                </div>
              )}
          </div>
          {isLocked ? (<img className={clsx('locked-icon', isWiggling && 'wiggle')} src={ICON.STAKED} alt="locked" />) : (
            <div className="rewards-container">
              <div className="text">Potential Rewards:</div>
              <div className="rewards">
                {rewards.map((reward, idx) => {
                  const key = `reward_${id}_${idx}_${reward.itemId}`;
                  return (
                    <div className="reward-container" id={`questcard-${key}`} key={key}>
                      <img
                        alt={key}
                        className="reward-icon"
                        src={`${LINKS.BACKPACK_BUCKET_URL}/${reward.itemId}.png`}
                        onError={({ currentTarget }) => { currentTarget.src = IMAGE.ITEM_PLACEHOLDER; }}
                      />
                      <div className="reward-amount-container">
                        <span className="reward-amount">{reward.amount}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {rewards.map((reward, idx) => {
        const key = `reward_${id}_${idx}_${reward.itemId}`;
        return (
          <InfoTooltip
            key={key}
            place="bottom"
            title={items[reward.itemId]?.name}
            anchorSelect={`#questcard-${key}`}
            description={items[reward.itemId]?.info}
          />
        );
      })}
    </button>
  );
}
